import { AsyncPipe } from '@angular/common';
import { Component } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BudgetAggregate } from '@core/models/budget-aggregate.model';
import { Plannable } from '@core/models/plannable.model';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DraggableCardComponent } from '@portal-shared/app/components/draggable-card/draggable-card.component';
import { FormatNumberPipe } from '@portal-shared/app/pipes/format-number.pipe';
import { PlannablesFiltersService } from '@resource-management/task-board/header/plannables-filter/plannables-filters.service';
import { PlannableGrouping } from '@resource-management/task-board/models/plannable-grouping.enum';
import { DragDropPayload } from '@services/models/drag-drop-payload.model';
import { ClassHelper } from '@shared/helpers/class.helper';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { Observable, map, of } from 'rxjs';

@Component({
  selector: 'al-plannable-card',
  templateUrl: './plannable-card.component.html',
  styleUrls: ['./plannable-card.component.scss'],
  imports: [AsyncPipe, TranslateModule, MatTooltipModule, FontAwesomeModule, DraggableCardComponent, FormatNumberPipe]
})
export class PlannableCardComponent implements ICellRendererAngularComp {
  public static rowHeightPx = 55;

  public levelClass: string = ClassHelper.portalLevel(0);

  public plannable?: Plannable;
  private groups: PlannableGrouping[] = [];

  public get payload(): DragDropPayload {
    return {
      plannable: this.plannable
    };
  }

  public cardTitle = '';
  public cardSubtitle = '';
  public isCardSubtitleDepartment = false;

  public get cardTooltip(): Observable<string> {
    if (!this.plannable) {
      return of('');
    }

    return PlannableCardComponent.formatBudgetTooltip(this.plannable, this.translateService);
  }

  public constructor(
    private translateService: TranslateService,
    plannablesFilterService: PlannablesFiltersService
  ) {
    this.groups = plannablesFilterService.getFilters().groupSelection;
  }

  public agInit(params: ICellRendererParams<Plannable, unknown, unknown>): void {
    this.refresh(params);
  }
  public refresh(params: ICellRendererParams<Plannable, unknown, unknown>): boolean {
    this.levelClass = ClassHelper.portalLevel(params.node.level);

    if (!params.data) {
      throw new Error('plannable null');
    }
    this.plannable = params.data;
    this.cardTitle = PlannableCardComponent.selectCardTitle(this.plannable, this.groups);
    this.cardSubtitle = this.selectCardSubTitle(this.plannable, this.groups);

    return true;
  }

  public static formatBudgetTooltip(plannable: Plannable | BudgetAggregate, translateService: TranslateService) {
    const formattedHours = [`${plannable.budgetedHours}`, `-${plannable.actualHours}`, `-${plannable.plannedHours}`];
    const [budgetedHours, actualHours, plannedHours] = formattedHours;

    const translationKeys = [
      'resource-management.taskBoard.tooltips.budgeted',
      plannable.actualHours ? 'resource-management.taskBoard.tooltips.actual' : '',
      plannable.plannedHours ? 'resource-management.taskBoard.tooltips.planned' : ''
    ].filter(Boolean);

    return translateService
      .stream(translationKeys, { budgetedHours, actualHours, plannedHours })
      .pipe(map((translations: Record<string, string>) => translationKeys.map(k => translations[k]).join('\n')));
  }

  public static selectCardTitle(plannable: Plannable | undefined, groups: string[]): string {
    if (!plannable) return '';

    const groupByProject = groups.includes('project');
    const groupByClass = groups.includes('class');
    const groupByDepartment = groups.includes('department');

    if (groups.length === 1 && groupByProject) {
      return plannable.classification.label;
    }

    if (groups.length === 2) {
      if (groupByClass) {
        return plannable.department.label;
      }
      if (groupByDepartment) {
        return plannable.classification.label;
      }
    }

    return plannable.project.label;
  }

  private selectCardSubTitle(plannable: Plannable | undefined, groups: string[]): string {
    this.isCardSubtitleDepartment = false;

    if (!plannable || groups.length === 2) {
      return '';
    }

    const groupByDepartment = groups.includes('department');
    if ((groups.length === 1 && groupByDepartment) || !groups.length) {
      return plannable.classification.label;
    }

    this.isCardSubtitleDepartment = true;
    return plannable.department.label;
  }
}
